import { LANGUAGES } from '../constants/languages'
import { getContrastColor } from './colors.helper'

const diff = require('diff')

export const copyToClipboard = text => {
    const textArea = document.createElement('textarea')
    textArea.value = text

    // Avoid scrolling to bottom
    textArea.style.top = '-1000px'
    textArea.style.left = '-1000px'
    textArea.style.position = 'absolute'

    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    var result

    try {
        result = document.execCommand('copy')
    } catch (err) {
        result = false
    }

    document.body.removeChild(textArea)

    return result
}

export const isASCII = str => {
    if (typeof str !== 'string') {
        return false
    }
    for (let i = 0; i < str.length; i++) {
        if (str.charCodeAt(i) > 127) {
            return false
        }
    }
    return true
}

export const doesStringRepresentBoolean = str => ['true', 'false'].includes(str.toLowerCase())
export const doesStringRepresentNumber = str => !isNaN(Number(str))

export const isValidHttpUrl = string => {
    let url
    try {
        url = new URL(string)
    } catch (_) {
        return false
    }
    return url.protocol === 'http:' || url.protocol === 'https:'
}

export const highlightDifferences = (actualOutput, expectedOutput, ignoreCase = false) => {
    // const formattedActualOutput = actualOutput
    const formattedActualOutput = actualOutput.replace(/<br\/>/g, '\n').replace(/&nbsp;/g, ' ')
    const formattedExpectedOutput = expectedOutput
    let output = ''
    let expected = ''

    const differences = diff.diffChars(formattedActualOutput, formattedExpectedOutput, {
        ignoreCase,
    })
    differences.forEach(part => {
        part.value = part.value.replace(/\n/g, '<br/>').replace(/ /g, '&nbsp;')
        if (part.added) {
            expected += `<ins title="Added">${part.value}</ins>`
        } else if (part.removed) {
            output += `<del title="Removed">${part.value}</del>`
        } else {
            output += part.value
            expected += part.value
        }
    })

    console.log(output)
    return { output, expectedOutput: expected }
}

/**
 * Highlights inline differences between old and new text
 * Returns HTML string with deletions (strikethrough) and additions (green text)
 * @param {string} oldText - The previous version of the text
 * @param {string} newText - The current version of the text
 * @returns {string} HTML string with inline diff markup
 */
export const highlightInlineDiff = (oldText, newText) => {
    if (!oldText || !newText) {
        return newText || oldText || ''
    }

    const differences = diff.diffWords(oldText, newText)
    let result = ''

    differences.forEach(part => {
        // Escape HTML entities to prevent XSS
        const escapedValue = part.value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;')

        if (part.added) {
            // Green text for additions (#2E7D32)
            result += `<ins title="Added">${escapedValue}</ins>`
        } else if (part.removed) {
            // Strikethrough for deletions
            result += `<del title="Removed">${escapedValue}</del>`
        } else {
            // Unchanged text
            result += escapedValue
        }
    })

    return result
}

export const getNameInitials = name =>
    name
        ?.split(' ')
        ?.map(word => word[0]?.toUpperCase())
        ?.join('')
        ?.replace('(', '')

export const convertPatternsListToHighlightRanges = (text, patternsList, language) => {
    // Only process for text languages
    if (language !== LANGUAGES.TEXT.value && language !== LANGUAGES.NEW_TEXT.value) {
        return []
    }

    if (!text || !patternsList || !Array.isArray(patternsList)) {
        return []
    }

    // Convert HTML to plain text while preserving line breaks
    const tempDiv = document.createElement('div')

    // First, replace HTML line break elements with actual newlines
    let processedText = text
        // Replace <br> and <br/> tags with newlines
        .replace(/<br\s*\/?>/gi, '\n')
        // Replace closing block-level tags with newlines
        .replace(/<\/(p|div|h[1-6]|li|tr|td|th|blockquote)>/gi, '\n')
        // Replace opening <p> tags with newlines (except the first one)
        .replace(/<p[^>]*>/gi, '\n')
        // Clean up multiple consecutive newlines
        .replace(/\n\s*\n/g, '\n')

    tempDiv.innerHTML = processedText
    const plainText = (tempDiv.textContent || tempDiv.innerText || '').trim()

    console.log('Original HTML:', text)
    console.log('Plain text with line breaks:', plainText)

    // Split into lines
    const lines = plainText.split('\n')

    // Calculate line start positions (character index where each line starts)
    const lineStartPositions = [0]
    for (let i = 0; i < lines.length - 1; i++) {
        lineStartPositions.push(lineStartPositions[i] + lines[i].length + 1) // +1 for newline character
    }

    // Convert patterns to highlight ranges
    const highlightRanges = []

    patternsList.forEach(pattern => {
        const { startRow, endRow, color } = pattern

        // Convert 1-based rows to 0-based indices
        const startLineIndex = Math.max(0, startRow - 1)
        const endLineIndex = Math.min(lines.length - 1, endRow - 1)

        // Validate line indices
        if (startLineIndex >= lines.length || endLineIndex >= lines.length || startLineIndex > endLineIndex) {
            return
        }

        // Calculate character positions
        const start = lineStartPositions[startLineIndex]
        let end

        if (startLineIndex === endLineIndex) {
            // Single line: end at the end of the line
            end = lineStartPositions[startLineIndex] + lines[startLineIndex].length
        } else {
            // Multiple lines: end at the end of the last line
            end = lineStartPositions[endLineIndex] + lines[endLineIndex].length
        }

        // Only add if we have a valid range
        if (start < end && end <= plainText.length) {
            highlightRanges.push({
                start,
                end,
                color: color + '50',
            })
        }
    })

    return highlightRanges
}
